import {DilutionAssignmentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
  VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {extractDuplicateNames} from 'procedures/design/validation/procedureValidationUtils';
import {DilutionPlan, isInaccessibleProcedureTestDefinition, Method} from 'procedures/ProcedureDomainTypes';

export const INVALID_DILUTION_ASSIGNMENT_ERROR_MESSAGE =
  'Multi-point dilution assignment requires two or more dilution factors to be specified';

export function validateDilutionFactor(dilutionFactor: number | null): string | null {
  // NaN values are silently filtered out
  if (Number.isNaN(dilutionFactor)) {
    return null;
  }
  if (dilutionFactor == null || !Number.isInteger(dilutionFactor) || dilutionFactor <= 0) {
    return 'Dilution factor must be a positive integer';
  }
  return null;
}

export function isValidDilutionAssignment(dilutionPlan: DilutionPlan): boolean {
  return (
    dilutionPlan.dilutionAssignment !== DilutionAssignmentType.MULTI_POINT ||
    dilutionPlan.allowedDilutionFactors.length >= 2
  );
}

export function validateMethodDilutionPlan(method: Method): Array<ProcedureValidationError> {
  const parentObjects = [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}];
  const dilutionFactorStrings = method.dilutionPlan.allowedDilutionFactors.map((dilutionFactor) =>
    dilutionFactor.toString()
  );
  const duplicateDilutionFactors = extractDuplicateNames(dilutionFactorStrings);

  const duplicateDilutionFactorsInMethodErrors = duplicateDilutionFactors.map((dilutionFactor) => ({
    errorMessage: 'Dilution factors must be unique within a unit operation',
    type: ProcedureValidationErrorType.DUPLICATE_DILUTION_FACTOR,
    primaryObject: {id: dilutionFactor, objectType: ProcedureValidationObjectType.DILUTION_PLAN},
    parentObjects,
  }));

  const dilutionFactorErrors = method.dilutionPlan.allowedDilutionFactors
    .map((dilutionFactor) => ({error: validateDilutionFactor(dilutionFactor), dilutionFactor}))
    .filter(({error}) => error != null);
  const invalidDilutionFactorErrors = dilutionFactorErrors.map(({error, dilutionFactor}) => ({
    errorMessage: error!,
    type: ProcedureValidationErrorType.INVALID_DILUTION_FACTOR,
    primaryObject: {id: dilutionFactor.toString(), objectType: ProcedureValidationObjectType.DILUTION_PLAN},
    parentObjects,
  }));

  const hasNoTestDefinitions =
    method.testDefinitions.length === 0 ||
    method.testDefinitions.every(
      (testDef) => !isInaccessibleProcedureTestDefinition(testDef) && testDef.id == null
    );
  const dilutionPlanWithoutTestDefinitionsError =
    hasNoTestDefinitions && method.dilutionPlan.allowedDilutionFactors.length > 0
      ? [
          {
            errorMessage: 'Test definitions must be specified to set dilution plan',
            type: ProcedureValidationErrorType.DILUTION_PLAN_WITHOUT_TEST_DEFINITIONS,
            primaryObject: {
              id: VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
              objectType: ProcedureValidationObjectType.DILUTION_PLAN,
            },
            parentObjects,
          },
        ]
      : [];

  const dilutionAssignmentErrors = !isValidDilutionAssignment(method.dilutionPlan)
    ? [
        {
          errorMessage: INVALID_DILUTION_ASSIGNMENT_ERROR_MESSAGE,
          type: ProcedureValidationErrorType.INVALID_DILUTION_ASSIGNMENT,
          primaryObject: {
            id: method.dilutionPlan.dilutionAssignment,
            objectType: ProcedureValidationObjectType.DILUTION_PLAN,
          },
          parentObjects,
        },
      ]
    : [];

  return [
    ...dilutionPlanWithoutTestDefinitionsError,
    ...duplicateDilutionFactorsInMethodErrors,
    ...invalidDilutionFactorErrors,
    ...dilutionAssignmentErrors,
  ];
}
